import axios from 'axios';
import {
    CollectionDataRequestParams,
    CollectionDataResponse,
    DocumentData,
    UploadDocumentResponse
} from "../models/collections.models";
import {DataSource} from "../models/models";

// TODO: save here more endpoints with collections and documents
// TODO: verify service and add lost functions
class CollectionService {
    private isLoading = false;
    private cachedContent: CollectionDataResponse | null = null;
    private cacheTimestamp: number | null = null;
    private readonly CACHE_DURATION = 5 * 60 * 1000;
    private pendingRequest: Promise<CollectionDataResponse > | null = null;

    async fetchCollections(page = 0, size = 1000, omitCache = false): Promise<CollectionDataResponse | null> {

        if (this.cachedContent && this.cacheTimestamp) {
            const now = Date.now();

            if ((now - this.cacheTimestamp < this.CACHE_DURATION) && !omitCache) {
                return this.cachedContent;
            }
        }

        if (this.pendingRequest) {
            return this.pendingRequest;
        }

        this.isLoading = true;

        this.pendingRequest = (async () => {
            try {
                const params = new URLSearchParams();
                params.append('page', String(page));
                params.append('size', String(size));

                const response = await axios.get('/documents/by-collection', { params });

                this.cachedContent = response.data;
                this.cacheTimestamp = Date.now();

                return response.data;
            } finally {
                this.isLoading = false;
                this.pendingRequest = null;
            }
        })();

        return this.pendingRequest;
    }

    async fetchDefaultCollection({page, size}: CollectionDataRequestParams): Promise<CollectionDataResponse> {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('size', String(size));
        params.append('isDefault', String(true));

        const response = await axios.get('/documents/by-collection/default', { params });
        return response.data;
    }

    async fetchCollection(collectionId: string) {
        return await axios.get<DataSource>(`/collections/${collectionId}`);
    }

    async updateCollectionDefaultFlag(id: number, isDefault: boolean): Promise<void> {
        await axios.patch(`/collections/${id}`, null, {
            params: { isDefault }
        });
        this.invalidateCache();
    }

    async updateSourceName(sourceId: number, newName: string): Promise<void> {
        await axios.patch(`/collections/${sourceId}/name`, null, {
            params: { collectionName: newName }
        });

        this.invalidateCache();
    }

    async uploadDocument(files: File[], collectionId?: string): Promise<UploadDocumentResponse> {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append(`files`, file);
        });
        if (collectionId) {
            formData.append('collectionId', collectionId);
        }

        const response = await axios.post<UploadDocumentResponse>('/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    }

    async deleteSource(sourceId: number): Promise<void> {
        await axios.delete(`/collections/${sourceId}/documents`);
        this.invalidateCache();
    }

    async deleteDocument(documentId: number): Promise<void> {
        await axios.delete(`/documents/${documentId}`);
        this.invalidateCache();
    }

    async fetchDocument(documentId: number): Promise<DocumentData> {
        const response = await axios.get(`/documents/${documentId}`);
        return response.data;
    }

    async getDocumentFile(documentId: number, fileName: string): Promise<File> {
        const res = await axios.get(`/documents/${documentId}/download`, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: res.headers['content-type'] });

        return new File([blob], fileName, { type: res.headers['content-type'] });
    }

    invalidateCache() {
        this.cachedContent = null;
        this.cacheTimestamp = null;
    }

    isRequestInProgress(): boolean {
        return this.isLoading;
    }
}

export const collectionService = new CollectionService();
